import { Box3, Vector3 } from 'three/webgpu'
import { types } from '@theatre/core'
import ImportGltf from '../utils/ImportGltf'
import DomTracked from './DomTracked'
import PointerTilt from './PointerTilt'
import staticPath from '../utils/staticPath'
import AgxPhysicalMaterial from '../materials/AgxPhysicalMaterial'
import DeviceScreen from './DeviceScreen'
import DeviceControls from './DeviceControls'
import ShadowCatcher from './ShadowCatcher'

export default class Device extends DomTracked {
	static defaults = {
		url: staticPath('models/device.glb'),
		renderer: null,
		listener: null,
		hideSource: false,
		cull: false,
		fit: 1.25,
		rotation: { x: 0, y: 0, z: 0 },
		ao: 1,
		exposure: 1,
		angle: { range: [-Math.PI, Math.PI] },
		nudge: { nudgeMultiplier: 0.001 },
		screen: {},
		screenMesh: 'screen_baked',
		project: null,
		controls: {},
		tuning: 0.22,
		shadow: { distance: 1.2 },
		videoSelector: '[data-device-video]',
	}

	constructor(options = {}) {
		super(options)

		this.wrapper = new PointerTilt({ rotation: this.options.rotation })
		this.add(this.wrapper)
		this.unbind = []
	}

	async init(onProgress) {
		this.importer = new ImportGltf(this.options.url, { renderer: this.options.renderer })

		this.model = await this.importer.load(onProgress)
		this.model.rotation.y = -Math.PI / 2
		this.#frame()
		this.#materials(material => AgxPhysicalMaterial.adopt(material, { exposure: this.options.exposure }))
		this.ao = this.options.ao
		this.wrapper.add(this.model)
		this.#shadow()
		this.#controls()
		this.#bindMeshes()
		this.#screen()

		return this
	}

	set ao(intensity) {
		this.options.ao = intensity
		this.#materials(material => (material.aoMapIntensity = intensity))
	}

	set exposure(value) {
		this.options.exposure = value
		this.#materials(material => material.toneMappingExposure && (material.toneMappingExposure.value = value))
	}

	mesh(name) {
		return this.model?.getObjectByName(name)
	}

	control(name) {
		return this.inputs?.controls.find(control => control.name.toLowerCase().includes(name))
	}

	#materials(apply) {
		this.model?.traverse(child => {
			for (const material of [child.material ?? []].flat()) {
				apply(material)
			}
		})
	}

	#meshes() {
		const meshes = []

		this.model?.traverse(child => {
			if (child.isMesh) meshes.push(child)
		})

		return meshes
	}

	#frame() {
		const bounds = new Box3().setFromObject(this.model)
		const size = bounds.getSize(new Vector3())
		const centre = bounds.getCenter(new Vector3())
		const scale = this.options.fit / Math.max(size.x, size.y, size.z)

		this.model.scale.multiplyScalar(scale)
		this.model.position.copy(centre).multiplyScalar(-scale)
	}

	#shadow() {
		const { distance, ...options } = this.options.shadow

		this.shadow = new ShadowCatcher(options)
		this.shadow.position.z = -distance

		this.add(this.shadow)
		this.unbind.push(this.shadow.bindStudio(this.sheet) ?? (() => {}))
	}

	#controls() {
		this.inputs = new DeviceControls({
			model: this.model,
			camera: this.camera,
			project: this.options.project,
			listener: this.options.listener,
			...this.options.controls,
		})
	}

	#screen() {
		const source = this.mesh(this.options.screenMesh)
		const video = this.element.querySelector(this.options.videoSelector)

		if (!source || !video) return

		this.screen = new DeviceScreen({ source, video, material: this.options.screen })
		this.unbind.push(this.screen.bindStudio(this.sheet) ?? (() => {}))
	}

	#bindMeshes() {
		if (!this.sheet) return

		for (const mesh of this.#meshes()) {
			if (mesh.parent?.parent === this.inputs) continue

			const origin = mesh.position.clone()
			const orient = mesh.rotation.clone()

			const control = this.sheet.object(`Device / ${mesh.name}`, {
				position: types.compound({
					x: types.number(origin.x, this.options.nudge),
					y: types.number(origin.y, this.options.nudge),
					z: types.number(origin.z, this.options.nudge),
				}),
				rotation: types.compound({
					x: types.number(orient.x, this.options.angle),
					y: types.number(orient.y, this.options.angle),
					z: types.number(orient.z, this.options.angle),
				}),
			})

			this.unbind.push(
				control.onValuesChange(({ position, rotation }) => {
					mesh.position.set(position?.x ?? origin.x, position?.y ?? origin.y, position?.z ?? origin.z)
					mesh.rotation.set(rotation?.x ?? orient.x, rotation?.y ?? orient.y, rotation?.z ?? orient.z)
				})
			)
		}
	}

	bindStudio(sheet) {
		if (!sheet) return

		this.sheet = sheet

		const base = this.options.rotation

		this.controls = sheet.object('Device', {
			rotation: types.compound({
				x: types.number(base.x, this.options.angle),
				y: types.number(base.y, this.options.angle),
				z: types.number(base.z, this.options.angle),
			}),
			ao: types.number(this.options.ao, { range: [0, 3] }),
			exposure: types.number(this.options.exposure, { range: [0, 3] }),
		})

		this.unbind.push(
			this.controls.onValuesChange(({ rotation, ao, exposure }) => {
				this.wrapper.base.set(rotation?.x ?? base.x, rotation?.y ?? base.y, rotation?.z ?? base.z)
				this.ao = ao ?? this.options.ao
				this.exposure = exposure ?? this.options.exposure
			})
		)
	}

	update(delta, elapsed) {
		super.update(delta, elapsed)
		this.wrapper.update(delta, elapsed)
		this.inputs?.update(delta, elapsed)
		this.screen?.update(delta, elapsed)

		if (this.screen && this.inputs?.dial) {
			this.screen.interference = Math.min(1, this.inputs.dial.speed * this.options.tuning)
		}
	}

	dispose() {
		super.dispose()
		this.wrapper.dispose()

		for (const unbind of this.unbind) unbind()

		this.unbind.length = 0
		this.screen?.dispose()
		this.screen = null
		this.inputs?.dispose()
		this.inputs = null
		this.shadow?.dispose()
		this.shadow = null
		this.importer?.dispose()
		this.importer = null
		this.model = null
	}
}
